import React, { useContext, useMemo, useState } from 'react';
import Modal from '@atlaskit/modal-dialog';
import styled from 'styled-components';

import DashboardsContext from 'contexts/DashboardsContext';
import ReportsContext from 'contexts/ReportsContext';
import { V3AppsContext } from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/V3AppsProvider';
import Button from 'kingpin/atoms/Button';
import Icon from 'kingpin/atoms/Icon';
import Typography from 'kingpin/atoms/Typography';
import Form from 'kingpin/forms/Form';
import FormContent from 'kingpin/forms/FormContent';
import FormHeader from 'kingpin/forms/FormHeader';
import Row from 'components/Common/Row';
import Search from 'components/Search';
import TextInput from 'kingpin/atoms/TextInput';

import { AddTarget, WorkspaceTarget } from './useCreateInsightsContent';

const Toggle = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
`;

const Section = styled.div`
  margin-bottom: 16px;
`;

const List = styled.div`
  margin-bottom: 16px;
  height: 220px;
  overflow-y: auto;
  padding-right: 12px;
`;

const Item = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 8px;
  cursor: pointer;
  user-select: none;
  height: 32px;
  &:hover {
    background-color: var(--color-bg-hover);
  }
`;

// Picker for an add_to_workspace proposal (3c): choose a V3 workspace, then
// either wrap the chat chart(s) in a NEW dashboard (named here) or add an
// EXISTING dashboard/report. Resolves to a WorkspaceTarget the card commits.
const AddToWorkspaceModal = ({
  chartCount,
  isSaving,
  defaultWorkspaceId,
  defaultTitle,
  onPick,
  onClose,
}: {
  chartCount: number;
  isSaving: boolean;
  defaultWorkspaceId?: string;
  defaultTitle?: string;
  onPick: (target: WorkspaceTarget) => void;
  onClose: () => void;
}) => {
  const { availableWorkspaces } = useContext(V3AppsContext);
  const { allDashboards } = useContext(DashboardsContext);
  const { availableReports } = useContext(ReportsContext);

  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<
    string | undefined
  >(defaultWorkspaceId);
  const [mode, setMode] = useState<'new' | 'existing'>('new');
  const [title, setTitle] = useState(defaultTitle || 'Insights');
  const [tab, setTab] = useState<'dashboard' | 'report'>('dashboard');
  const [searchText, setSearchText] = useState('');
  const [selectedDashboard, setSelectedDashboard] = useState<
    DashboardType | undefined
  >();
  const [selectedReport, setSelectedReport] = useState<
    ReportType | undefined
  >();

  const filteredDashboards = useMemo(() => {
    const lower = searchText.toLowerCase();
    return allDashboards
      .filter((d) => !d.isWallboardSlide && !d.templateId)
      .filter((d) => d.name.toLowerCase().includes(lower));
  }, [allDashboards, searchText]);
  const filteredReports = useMemo(() => {
    const lower = searchText.toLowerCase();
    return availableReports.filter((r) => r.name.toLowerCase().includes(lower));
  }, [availableReports, searchText]);

  const items: (DashboardType | ReportType)[] =
    tab === 'dashboard' ? filteredDashboards : filteredReports;
  const selectedItemId =
    tab === 'dashboard' ? selectedDashboard?.id : selectedReport?.id;

  const chartLabel = chartCount === 1 ? '1 chart' : `${chartCount} charts`;
  const workspace = availableWorkspaces.find(
    (w) => w.id === selectedWorkspaceId,
  );

  const existingItem: AddTarget | undefined =
    tab === 'dashboard' && selectedDashboard
      ? { kind: 'dashboard', entity: selectedDashboard }
      : tab === 'report' && selectedReport
        ? { kind: 'report', entity: selectedReport }
        : undefined;

  const canAdd =
    !!workspace && (mode === 'new' ? title.trim().length > 0 : !!existingItem);

  const onAdd = () => {
    if (!workspace) {
      return;
    }
    if (mode === 'new') {
      if (!title.trim()) {
        return;
      }
      onPick({
        kind: 'workspace',
        workspace,
        content: { kind: 'new', title: title.trim() },
      });
    } else if (existingItem) {
      onPick({
        kind: 'workspace',
        workspace,
        content: { kind: 'existing', item: existingItem },
      });
    }
  };

  return (
    <Modal onClose={onClose} shouldScrollInViewport={false} autoFocus={false}>
      <Form data-testid="add-to-workspace-flow">
        <FormHeader
          title={`Add ${chartLabel} to a workspace`}
          onClose={onClose}
        />
        <FormContent>
          <Section>
            <Typography.Body type="Body 13 Bold">Workspace</Typography.Body>
            <List style={{ height: 140 }}>
              {availableWorkspaces.map((w) => (
                <Item
                  key={w.id}
                  onClick={() =>
                    setSelectedWorkspaceId(
                      selectedWorkspaceId === w.id ? undefined : w.id,
                    )
                  }
                >
                  <Row
                    centerAlign
                    style={{ justifyContent: 'space-between', width: '100%' }}
                  >
                    <Typography.Body type="Body 12">{w.label}</Typography.Body>
                    {selectedWorkspaceId === w.id && <Icon icon="checkmark" />}
                  </Row>
                </Item>
              ))}
            </List>
          </Section>

          <Toggle>
            <Button
              size="Small"
              type={mode === 'new' ? 'Primary' : 'Ghost'}
              label="New dashboard"
              onClick={() => setMode('new')}
              testId="add-to-workspace-mode-new"
            />
            <Button
              size="Small"
              type={mode === 'existing' ? 'Primary' : 'Ghost'}
              label="Existing content"
              onClick={() => setMode('existing')}
              testId="add-to-workspace-mode-existing"
            />
          </Toggle>

          {mode === 'new' ? (
            <Section>
              <Typography.Body type="Body 13 Bold">
                New dashboard name
              </Typography.Body>
              <TextInput
                value={title}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setTitle(e.target.value)
                }
                testId="add-to-workspace-title"
              />
            </Section>
          ) : (
            <Section>
              <Toggle>
                <Button
                  size="Small"
                  type={tab === 'dashboard' ? 'Primary' : 'Ghost'}
                  label="Dashboard"
                  onClick={() => setTab('dashboard')}
                  testId="add-to-workspace-tab-dashboard"
                />
                <Button
                  size="Small"
                  type={tab === 'report' ? 'Primary' : 'Ghost'}
                  label="Report"
                  onClick={() => setTab('report')}
                  testId="add-to-workspace-tab-report"
                />
              </Toggle>
              <div style={{ marginBottom: 16 }}>
                <Search
                  noBorder
                  onSearchTextChanged={(
                    e: React.ChangeEvent<HTMLInputElement>,
                  ) => setSearchText(e.target.value)}
                  searchText={searchText}
                  onClearClicked={() => setSearchText('')}
                />
              </div>
              <List>
                {items.map((item) => (
                  <Item
                    key={item.id}
                    onClick={() => {
                      if (tab === 'dashboard') {
                        setSelectedDashboard(
                          selectedDashboard?.id === item.id
                            ? undefined
                            : (item as DashboardType),
                        );
                      } else {
                        setSelectedReport(
                          selectedReport?.id === item.id
                            ? undefined
                            : (item as ReportType),
                        );
                      }
                    }}
                  >
                    <Row
                      centerAlign
                      style={{ justifyContent: 'space-between', width: '100%' }}
                    >
                      <Typography.Body type="Body 12">
                        {item.name}
                      </Typography.Body>
                      {selectedItemId === item.id && <Icon icon="checkmark" />}
                    </Row>
                  </Item>
                ))}
              </List>
            </Section>
          )}
        </FormContent>
        <Row centerAlign spaceBetween style={{ marginBottom: 16 }}>
          <Button
            onClick={onClose}
            isDisabled={isSaving}
            label="Cancel"
            size="Small"
            type="Ghost"
          />
          <Button
            type="Primary"
            size="Small"
            label="Add"
            onClick={onAdd}
            isDisabled={isSaving || !canAdd}
            isLoading={isSaving}
            testId="add-to-workspace-confirm"
          />
        </Row>
      </Form>
    </Modal>
  );
};

export default AddToWorkspaceModal;
